import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useMasters } from '../../hooks/useMasters';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { useToast } from '../../components/ui/Toast';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Drawer, Field, FieldRow, Select, TextInput } from '../../components/ui/Form';
import { ImagePicker } from '../../components/ui/ImagePicker';
import { MasterLink } from '../../components/ui/MasterLink';

interface Product {
  id: string;
  version: number;
  image_data_uri?: string | null;
  sku: string;
  name: string;
  metal_id: string;
  default_purity_id: string | null;
  category_id: string | null;
  collection_id: string | null;
  design_id: string | null;
  brand_id: string | null;
  tax_group_id: string | null;
  pricing_rule_id: string | null;
  tracking_mode: string;
  hsn_code: string | null;
  is_active: boolean;
}

interface Named {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
}

const BLANK = {
  sku: '',
  name: '',
  metal_id: '',
  default_purity_id: '',
  category_id: '',
  collection_id: '',
  design_id: '',
  brand_id: '',
  tax_group_id: '',
  pricing_rule_id: '',
  hsn_code: '7113',
  tracking_mode: 'SERIAL',
  image_data_uri: '' as string,
};

/** A short list from Setup → Lists, offered only while it is in use. */
function useList(route: string) {
  const q = useQuery({
    queryKey: ['master', route],
    queryFn: () => api.get<{ data: Named[] }>(`/${route}?limit=200`),
    staleTime: 10_000,
  });
  return (q.data?.data ?? []).filter((r) => r.is_active !== false);
}

const asOptions = (rows: { id: string; code: string; name?: string }[]) =>
  rows.map((r) => ({ value: r.id, label: r.name ? `${r.name} (${r.code})` : r.code }));

/**
 * The catalogue: the kinds of thing the shop sells.
 *
 * A product can be corrected after it is made — its name, its photo, which
 * category and collection it is filed under, which GST rate and pricing rule it
 * uses. Its SKU, metal and tracking are fixed once made, because every tagged
 * piece and every invoice line already depends on them; a product that was
 * made wrong in one of those is retired and made again.
 */
export function ProductsPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const toast = useToast();
  const onEnter = useKeyboardForm();
  const masters = useMasters();
  const categories = useList('categories');
  const collections = useList('collections');
  const designs = useList('designs');
  const brands = useList('brands');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ ...BLANK });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [retiring, setRetiring] = useState<Product | null>(null);
  const [error, setError] = useState<unknown>(null);

  const mayEdit = can('product.edit');
  const mayRetire = can('document.void');

  const { data, isLoading } = useQuery({
    queryKey: ['products', search],
    queryFn: () => endpoints.masters.products(search || undefined),
  });

  const close = (): void => {
    setOpen(false);
    setEditing(null);
    setForm({ ...BLANK });
    setError(null);
  };

  const save = useMutation({
    mutationFn: () => {
      const shared = {
        name: form.name.trim(),
        default_purity_id: form.default_purity_id || null,
        category_id: form.category_id || null,
        collection_id: form.collection_id || null,
        design_id: form.design_id || null,
        brand_id: form.brand_id || null,
        tax_group_id: form.tax_group_id || null,
        pricing_rule_id: form.pricing_rule_id || null,
        hsn_code: form.hsn_code || null,
        image_data_uri: form.image_data_uri || null,
      };
      if (editing) return api.patch(`/products/${editing.id}`, shared, editing.version);
      // A new product sends only what was chosen; empty choices are left out.
      const chosen = Object.fromEntries(Object.entries(shared).filter(([, v]) => v !== null));
      return api.post('/products', {
        sku: form.sku,
        metal_id: form.metal_id,
        tracking_mode: form.tracking_mode,
        ...chosen,
      });
    },
    onSuccess: () => {
      toast(editing ? `${form.name} updated` : `${form.name} added`);
      void client.invalidateQueries({ queryKey: ['products'] });
      close();
    },
    onError: setError,
  });

  const retire = useMutation({
    mutationFn: (p: Product) => api.patch(`/products/${p.id}`, { is_active: !p.is_active }, p.version),
    onSuccess: (_d, p) => {
      toast(p.is_active ? `${p.name} retired` : `${p.name} is back in the catalogue`);
      void client.invalidateQueries({ queryKey: ['products'] });
      setRetiring(null);
    },
    onError: (e) => {
      setError(e);
      setRetiring(null);
    },
  });

  const startEdit = (p: Product): void => {
    setEditing(p);
    setForm({
      sku: p.sku,
      name: p.name,
      metal_id: p.metal_id,
      default_purity_id: p.default_purity_id ?? '',
      category_id: p.category_id ?? '',
      collection_id: p.collection_id ?? '',
      design_id: p.design_id ?? '',
      brand_id: p.brand_id ?? '',
      tax_group_id: p.tax_group_id ?? '',
      pricing_rule_id: p.pricing_rule_id ?? '',
      hsn_code: p.hsn_code ?? '',
      tracking_mode: p.tracking_mode,
      image_data_uri: p.image_data_uri ?? '',
    });
    setOpen(true);
  };

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (ready) save.mutate();
  };

  const rows = (data?.data ?? []) as unknown as Product[];
  const ready = form.sku.trim() !== '' && form.name.trim() !== '' && form.metal_id !== '';
  const nameOf = new Map(categories.map((c) => [c.id, c.name]));

  return (
    <>
      <PageHeader
        title="Products"
        subtitle={data ? `${rows.length} in the catalogue` : undefined}
        actions={
          <>
            <input
              className="field w-64"
              placeholder="Search SKU or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {can('product.create') ? (
              <button className="btn-primary whitespace-nowrap" onClick={() => setOpen(true)}>
                New product
              </button>
            ) : null}
          </>
        }
      />

      {!open ? <ErrorNotice error={error} /> : null}
      {isLoading ? <Loading /> : null}

      {!isLoading && rows.length === 0 ? (
        <Empty
          message={
            search
              ? 'No products match that search.'
              : 'Nothing in the catalogue yet. Add a product, then tag the pieces you hold.'
          }
        />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['', 'SKU', 'Name', 'Category', 'Tracking', 'HSN', 'Status', '']}>
          {rows.map((p) => (
            <tr key={p.id} className={p.is_active ? '' : 'opacity-60'}>
              <td className="td">
                {p.image_data_uri ? (
                  <img src={p.image_data_uri} alt="" className="h-8 w-8 rounded object-cover" />
                ) : (
                  <span className="grid h-8 w-8 place-items-center rounded bg-silver-50 text-2xs text-ink-faint">—</span>
                )}
              </td>
              <td className="td num">{p.sku}</td>
              <td className="td">{p.name}</td>
              <td className="td">{p.category_id ? nameOf.get(p.category_id) ?? '—' : '—'}</td>
              <td className="td">{p.tracking_mode === 'SERIAL' ? 'Tagged piece' : 'By weight'}</td>
              <td className="td num">{p.hsn_code ?? '—'}</td>
              <td className="td">
                {p.is_active ? <span className="text-success">Active</span> : <span className="text-ink-soft">Retired</span>}
              </td>
              <td className="td whitespace-nowrap text-right">
                {mayEdit && p.is_active ? (
                  <button className="text-2xs text-gold-700 underline" onClick={() => startEdit(p)}>
                    Edit
                  </button>
                ) : null}
                {mayRetire ? (
                  <button className="ml-3 text-2xs text-ink-soft underline" onClick={() => setRetiring(p)}>
                    {p.is_active ? 'Retire' : 'Bring back'}
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      {open ? (
        <Drawer
          title={editing ? `Edit ${editing.sku}` : 'New product'}
          subtitle={
            editing
              ? 'SKU, metal and tracking stay as they are — tagged pieces already depend on them.'
              : 'A product is the kind of thing you sell. Individual pieces are tagged separately, under Pieces in stock.'
          }
          onClose={close}
        >
          <ErrorNotice error={error} />

          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            <Field label="Photo" hint="Shown on the sell screen, so a piece is found by sight.">
              <ImagePicker
                value={form.image_data_uri || null}
                onChange={(uri) => setForm({ ...form, image_data_uri: uri ?? '' })}
              />
            </Field>

            <FieldRow>
              <Field label="SKU" required hint={editing ? undefined : 'Your own code. Must be unique.'}>
                <TextInput
                  value={form.sku}
                  onChange={(v) => setForm({ ...form, sku: v })}
                  placeholder="BNG-22K"
                  maxLength={32}
                  uppercase
                  allow={/[A-Z0-9\-_./]/}
                  autoFocus={!editing}
                  disabled={!!editing}
                />
              </Field>
              <Field label="Name" required>
                <TextInput
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                  placeholder="22K gold bangle"
                  maxLength={200}
                  autoFocus={!!editing}
                />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field label="Metal" required>
                <Select
                  value={form.metal_id}
                  onChange={(v) => setForm({ ...form, metal_id: v, default_purity_id: '' })}
                  options={masters.metalOptions}
                  disabled={!!editing}
                />
                {!editing ? <MasterLink to="/metals" label="New metal" /> : null}
              </Field>
              <Field label="Default purity">
                <Select
                  value={form.default_purity_id}
                  onChange={(v) => setForm({ ...form, default_purity_id: v })}
                  options={masters.puritiesFor(form.metal_id).map((p) => ({ value: p.id, label: p.code }))}
                  placeholder={form.metal_id ? 'Choose…' : 'Choose a metal first'}
                />
                <MasterLink to="/metals" label="New purity" />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field label="Category">
                <Select
                  value={form.category_id}
                  onChange={(v) => setForm({ ...form, category_id: v })}
                  options={asOptions(categories)}
                  placeholder={categories.length === 0 ? 'None set up yet' : 'Choose…'}
                />
                <MasterLink to="/lists?list=categories" label="New category" />
              </Field>
              <Field label="Collection">
                <Select
                  value={form.collection_id}
                  onChange={(v) => setForm({ ...form, collection_id: v })}
                  options={asOptions(collections)}
                  placeholder={collections.length === 0 ? 'None set up yet' : 'Choose…'}
                />
                <MasterLink to="/lists?list=collections" label="New collection" />
              </Field>
              <Field label="Design">
                <Select
                  value={form.design_id}
                  onChange={(v) => setForm({ ...form, design_id: v })}
                  options={asOptions(designs)}
                  placeholder={designs.length === 0 ? 'None set up yet' : 'Choose…'}
                />
                <MasterLink to="/lists?list=designs" label="New design" />
              </Field>
              <Field label="Brand">
                <Select
                  value={form.brand_id}
                  onChange={(v) => setForm({ ...form, brand_id: v })}
                  options={asOptions(brands)}
                  placeholder={brands.length === 0 ? 'None set up yet' : 'Choose…'}
                />
                <MasterLink to="/lists?list=brands" label="New brand" />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field label="GST rate" hint="Rates are dated, so old invoices keep their rate.">
                <Select
                  value={form.tax_group_id}
                  onChange={(v) => setForm({ ...form, tax_group_id: v })}
                  options={asOptions(masters.taxGroups)}
                  placeholder={masters.taxGroups.length === 0 ? 'None set up yet' : 'Choose…'}
                />
                <MasterLink to="/charges?tab=tax" label="New GST rate" />
              </Field>
              <Field label="Pricing rule" hint="Its making charge and wastage.">
                <Select
                  value={form.pricing_rule_id}
                  onChange={(v) => setForm({ ...form, pricing_rule_id: v })}
                  options={asOptions(masters.pricingRules)}
                  placeholder={masters.pricingRules.length === 0 ? 'None set up yet' : 'Choose…'}
                />
                <MasterLink to="/charges?tab=pricing" label="New pricing rule" />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field label="HSN code" hint="7113 for jewellery, 7108 for bullion.">
                <TextInput
                  value={form.hsn_code}
                  onChange={(v) => setForm({ ...form, hsn_code: v })}
                  maxLength={8}
                  allow={/[0-9]/}
                  placeholder="7113"
                />
              </Field>
              <Field label="Tracking" hint="Tagged pieces are counted one by one.">
                <Select
                  value={form.tracking_mode}
                  onChange={(v) => setForm({ ...form, tracking_mode: v || 'SERIAL' })}
                  options={[
                    { value: 'SERIAL', label: 'Tagged piece' },
                    { value: 'BULK', label: 'By weight' },
                  ]}
                  disabled={!!editing}
                />
              </Field>
            </FieldRow>

            <div className="flex gap-2 pt-2">
              <button className="btn-primary" disabled={!ready || save.isPending}>
                {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Save product'}
              </button>
              <button type="button" className="btn-ghost" onClick={close}>
                Cancel
              </button>
            </div>
          </form>
        </Drawer>
      ) : null}

      <ConfirmDialog
        open={!!retiring}
        tone={retiring?.is_active === false ? 'primary' : 'danger'}
        title={retiring?.is_active === false ? `Bring back ${retiring?.name ?? ''}?` : `Retire ${retiring?.name ?? ''}?`}
        message={
          retiring?.is_active === false
            ? 'It will be offered again on purchases and at the counter.'
            : 'Pieces already tagged and invoices already made stay exactly as they are. It simply stops being offered for new stock.'
        }
        confirmLabel={retiring?.is_active === false ? 'Bring it back' : 'Retire it'}
        busy={retire.isPending}
        onCancel={() => setRetiring(null)}
        onConfirm={() => retiring && retire.mutate(retiring)}
      />
    </>
  );
}
